<template>
  <div class="consumption">
    <section>
      <div class="is-flex is-align-items-center is-justify-content-space-between mb-5">
        <h3 class="title is-6 mb-0">Statistiques de l'étape</h3>
        <b-field class="mb-0">
          <b-select
            v-model="selectedStageId"
            expanded
            :disabled="!liveStages.length"
            :placeholder="liveStages.length ? 'Choisir une étape' : 'Aucune étape sur ce live'"
            icon="flag-checkered"
          >
            <option v-for="stage in liveStages" :key="stage.id" :value="stage.id">
              {{ stage.name }}
            </option>
          </b-select>
        </b-field>
      </div>
      <b-field class="mb-5">
        <b-input
          v-model="trackerKeyword"
          type="text"
          placeholder="Rechercher un tracker"
          icon="search"
          :icon-right="trackerKeyword ? 'times-circle' : ''"
          icon-right-clickable
          @icon-right-click="trackerKeyword = ''"
        />
      </b-field>
      <div class="consumption_chart mb-5">
        <b-loading :active="curvesLoading" :is-full-page="false" />
        <edit-live-review-consumption-chart
          :curves="visibleCurves"
          :selectedTrackerId="selectedTrackerId"
          @select="selectTracker"
        />
        <p
          v-if="!curvesLoading && !visibleCurves.length"
          class="consumption_chart_empty has-text-grey has-text-centered"
        >
          {{ visibleTrackerIds.length ? 'Aucune position à afficher.' : 'Tracker introuvable.' }}
        </p>
      </div>
      <edit-live-review-consumption-table
        :rows="statisticsRows"
        :loading="statisticsLoading"
        :selectedTrackerId="selectedTrackerId"
        :lowInitialBatteryPercentage="LOW_INITIAL_BATTERY_PERCENTAGE"
        :hasTrackers="visibleTrackerIds.length > 0"
        @select="selectTracker"
      />
    </section>

    <hr />

    <section>
      <div class="is-flex is-align-items-center is-justify-content-space-between mb-5">
        <h3 class="title is-6 mb-0">Batterie initiale des précédents lives</h3>
      </div>
      <edit-live-review-consumption-history-table
        :rows="historyRows"
        :columnLabels="historyColumnLabels"
        :loading="pastInitialBatteriesLoading"
        :lowInitialBatteryPercentage="LOW_INITIAL_BATTERY_PERCENTAGE"
      />
    </section>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { format } from 'date-fns'
import { stringNormalizer } from '@helpers/stringNormalizer'
import EditLiveReviewConsumptionChart from './EditLiveReviewConsumptionChart.vue'
import EditLiveReviewConsumptionTable from './EditLiveReviewConsumptionTable.vue'
import EditLiveReviewConsumptionHistoryTable from './EditLiveReviewConsumptionHistoryTable.vue'

const LOW_INITIAL_BATTERY_PERCENTAGE = 70

const DATE_FORMAT = 'dd/MM/yyyy'

const PREVIOUS_LIVES_COUNT = 5

const HISTORY_COLUMN_LABELS = [
  'Live courant',
  ...Array.from({ length: PREVIOUS_LIVES_COUNT }, (_, index) => `Live -${index + 1}`),
]

export default {
  name: 'EditLiveReviewConsumption',

  components: {
    EditLiveReviewConsumptionChart,
    EditLiveReviewConsumptionTable,
    EditLiveReviewConsumptionHistoryTable,
  },

  props: {
    live: {
      type: Object,
      required: true,
    },
    liveVehicles: {
      type: Array,
      required: true,
    },
    liveStages: {
      type: Array,
      required: true,
    },
    queclinkTrackers: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      curvesLoading: false,
      statisticsLoading: false,
      pastInitialBatteriesLoading: false,
      selectedStageId: null,
      selectedTrackerId: null,
      trackerKeyword: '',
      curves: [],
      statistics: [],
      pastInitialBatteries: [],
    }
  },

  created() {
    this.historyColumnLabels = HISTORY_COLUMN_LABELS
    this.LOW_INITIAL_BATTERY_PERCENTAGE = LOW_INITIAL_BATTERY_PERCENTAGE

    this.curvesByStageId = new Map()
    this.fetchingStageId = null
    this.abortController = null

    this.load()
  },

  beforeDestroy() {
    this.abortController?.abort()
  },

  watch: {
    selectedStageId: 'fetchCurves',

    visibleTrackerIds(visibleTrackerIds) {
      if (this.selectedTrackerId !== null && !visibleTrackerIds.includes(this.selectedTrackerId)) {
        this.selectedTrackerId = null
      }
    },
  },

  computed: {
    vehicleNumbersByTrackerId() {
      return Object.fromEntries(
        this.liveVehicles
          .filter((vehicle) => vehicle.queclinkTrackerId)
          .map((vehicle) => [vehicle.queclinkTrackerId, vehicle.number]),
      )
    },

    trackerNumbers() {
      return Object.fromEntries(this.queclinkTrackers.map(({ id, number }) => [id, number]))
    },

    trackerLabels() {
      return Object.fromEntries(
        this.queclinkTrackers.map(({ id, number }) => [
          id,
          this.vehicleNumbersByTrackerId[id]
            ? `${number} - ${this.vehicleNumbersByTrackerId[id]}`
            : number,
        ]),
      )
    },

    visibleCurves() {
      const visibleTrackerIds = new Set(this.visibleTrackerIds)
      const colorIndexes = Object.fromEntries(
        this.queclinkTrackers.map(({ id }, index) => [id, index]),
      )

      return this.curves
        .filter(({ id }) => visibleTrackerIds.has(id))
        .map(({ id, points }) => ({
          id,
          name: this.trackerLabels[id],
          colorIndex: colorIndexes[id],
          points,
        }))
    },

    visibleTrackerIds() {
      const trackerIds = this.queclinkTrackers.map(({ id }) => id)
      const keyword = stringNormalizer(this.trackerKeyword).toLowerCase()

      if (!keyword) {
        return trackerIds
      }

      return trackerIds.filter((trackerId) =>
        stringNormalizer(this.trackerLabels[trackerId] || '')
          .toLowerCase()
          .includes(keyword),
      )
    },

    statisticsByStageId() {
      const grouped = {}
      for (const statistics of this.statistics) {
        if (!grouped[statistics.liveStageId]) {
          grouped[statistics.liveStageId] = []
        }
        grouped[statistics.liveStageId].push(statistics)
      }

      return grouped
    },

    statisticsRows() {
      const stageStatistics = this.statisticsByStageId[this.selectedStageId] || []
      if (!stageStatistics.length) {
        return []
      }

      const statisticsByTrackerId = Object.fromEntries(
        stageStatistics.map((statistics) => [statistics.queclinkTrackerId, statistics]),
      )

      return this.visibleTrackerIds.map((trackerId) => {
        const battery = statisticsByTrackerId[trackerId]?.batteryStatistics

        return {
          trackerId,
          label: this.trackerLabels[trackerId],
          initialPercentage: battery?.initialPercentage ?? null,
          finalPercentage: battery?.finalPercentage ?? null,
          averageMotionConsumptionPerHour: battery?.averageMotionConsumptionPerHour ?? null,
          averageIdleConsumptionPerHour: battery?.averageIdleConsumptionPerHour ?? null,
          chargeCount: battery?.chargeCount ?? null,
          averageMotionIntervalSeconds: battery?.averageMotionIntervalSeconds ?? null,
        }
      })
    },

    firstLiveStage() {
      if (!this.liveStages.length) {
        return null
      }

      return this.liveStages.reduce((first, stage) =>
        stage.startedAt < first.startedAt ? stage : first,
      )
    },

    firstLiveStageId() {
      return this.firstLiveStage?.id ?? null
    },

    currentLiveStartedAt() {
      return this.firstLiveStage ? format(this.firstLiveStage.startedAt, DATE_FORMAT) : null
    },

    currentLiveCellsByTrackerId() {
      if (this.firstLiveStageId === null) {
        return {}
      }

      const firstStageStatistics = this.statisticsByStageId[this.firstLiveStageId] || []

      return Object.fromEntries(
        firstStageStatistics.map((statistics) => [
          statistics.queclinkTrackerId,
          {
            liveId: null,
            liveName: this.live.name,
            startedAt: this.currentLiveStartedAt,
            vehicleNumber: this.vehicleNumbersByTrackerId[statistics.queclinkTrackerId] || null,
            initialPercentage: statistics.batteryStatistics.initialPercentage,
          },
        ]),
      )
    },

    historyRows() {
      const pastInitialBatteriesByTrackerId = {}
      for (const entry of this.pastInitialBatteries) {
        if (!pastInitialBatteriesByTrackerId[entry.trackerId]) {
          pastInitialBatteriesByTrackerId[entry.trackerId] = []
        }
        pastInitialBatteriesByTrackerId[entry.trackerId].push(entry)
      }

      return this.queclinkTrackers.map(({ id: trackerId }) => {
        const entries = pastInitialBatteriesByTrackerId[trackerId] || []
        const previousLives = entries.map((entry) => ({
          liveId: entry.liveId,
          liveName: entry.liveName,
          startedAt: format(entry.liveStartedAt, DATE_FORMAT),
          vehicleNumber: entry.vehicleNumber,
          initialPercentage: entry.initialPercentage,
        }))

        return {
          trackerId,
          label: this.trackerNumbers[trackerId],
          cells: [
            this.currentLiveCellsByTrackerId[trackerId] || null,
            ...Array.from(
              { length: PREVIOUS_LIVES_COUNT },
              (_, index) => previousLives[index] || null,
            ),
          ],
        }
      })
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    load() {
      this.abortController?.abort()
      this.curvesByStageId.clear()
      this.fetchingStageId = null
      this.selectedStageId = null
      this.selectedTrackerId = null
      this.trackerKeyword = ''
      this.curves = []
      this.statistics = []
      this.pastInitialBatteries = []

      this.selectedStageId = this.firstLiveStageId
      this.fetchStatistics()
      this.fetchPastInitialBatteries()
    },

    selectTracker(trackerId) {
      this.selectedTrackerId = trackerId === this.selectedTrackerId ? null : trackerId
    },

    async fetchStatistics() {
      this.statisticsLoading = true

      try {
        this.statistics = await this.$services.liveQueclinkTrackerStatisticsService.getAllByLiveId(
          this.live.id,
        )
      } catch (error) {
        this.addToastMessage({ text: error.message, type: 'is-danger' })
      } finally {
        this.statisticsLoading = false
      }
    },

    async fetchPastInitialBatteries() {
      this.pastInitialBatteriesLoading = true

      try {
        this.pastInitialBatteries =
          await this.$services.queclinkTrackerStatisticsHistoryService.getAllInitialBatteriesByLiveId(
            this.live.id,
          )
      } catch (error) {
        this.addToastMessage({ text: error.message, type: 'is-danger' })
      } finally {
        this.pastInitialBatteriesLoading = false
      }
    },

    async fetchCurves() {
      const liveStage = this.liveStages.find((stage) => stage.id === this.selectedStageId)
      if (!liveStage || this.fetchingStageId === liveStage.id) {
        return
      }

      this.abortController?.abort()

      const cached = this.curvesByStageId.get(liveStage.id)
      if (cached) {
        this.curves = cached
        return
      }

      this.abortController = new AbortController()
      const { signal } = this.abortController

      this.curves = []
      this.curvesLoading = true
      this.fetchingStageId = liveStage.id

      try {
        const stream =
          this.$services.queclinkTrackerChartSeriesService.getAllBatteryTimelinesByLiveId(
            this.live.id,
            liveStage.startedAt,
            liveStage.endedAt,
            { signal },
          )

        for await (const batch of stream) {
          this.curves = [
            ...this.curves,
            ...batch.map(({ id, points }) => ({
              id,
              points: points.map((point) => ({ x: point.x * 1000, y: point.y })),
            })),
          ]
        }

        this.curvesByStageId.set(liveStage.id, this.curves)
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.addToastMessage({ text: error.message, type: 'is-danger' })
        }
      } finally {
        // an aborted run must not clear the state the run that replaced it just set up
        if (this.abortController?.signal === signal) {
          this.fetchingStageId = null
          this.curvesLoading = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.consumption {
  &_chart {
    position: relative;
    height: 40vh;
  }

  &_chart_empty {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
  }
}
</style>
